import { useState } from 'react';
import { Link } from 'react-router';
import { PageContainer } from '@/components/common/page-container';
import { PageHeader } from '@/components/common/page-header';
import { usePaquetesPendientes, usePaquetesListar } from '@/lib/hooks/use-paquetes';
import { EstadoPaqueteBadge } from '@/components/paquetes/estado-badge';
import { RetirarDialog } from '@/components/paquetes/retirar-dialog';
import { DevolverDialog } from '@/components/paquetes/devolver-dialog';
import { AnularDialog } from '@/components/paquetes/anular-dialog';
import type { PaquetePublic } from '@condo/contracts';

type Tab = 'pendientes' | 'historial';

type DialogState =
  | { type: 'retirar'; paquete: PaquetePublic }
  | { type: 'devolver'; paquete: PaquetePublic }
  | { type: 'anular'; paquete: PaquetePublic }
  | null;

export function PaquetesConserjeView() {
  const [tab, setTab] = useState<Tab>('pendientes');
  const [dialog, setDialog] = useState<DialogState>(null);
  const pendientes = usePaquetesPendientes();
  const historial = usePaquetesListar();

  const rows: PaquetePublic[] =
    tab === 'pendientes' ? (pendientes.data ?? []) : (historial.data ?? []);

  const isLoading =
    tab === 'pendientes' ? pendientes.isLoading : historial.isLoading;

  return (
    <PageContainer width="wide">
      <PageHeader
        eyebrow="Encomiendas"
        title="Paquetes"
        action={
          <Link
            to="nuevo"
            className="inline-flex items-center gap-1 rounded-md bg-[var(--color-primary)] px-4 py-2 text-sm font-semibold text-white hover:bg-[var(--color-primary-hover,#0b5c69)] transition-colors"
          >
            + Registrar nuevo
          </Link>
        }
      />

      {/* Tabs */}
      <div className="flex gap-6 border-b border-[var(--color-hairline)] mb-6">
        <button
          type="button"
          onClick={() => setTab('pendientes')}
          className={[
            'pb-3 text-sm font-semibold border-b-2 transition-colors',
            tab === 'pendientes'
              ? 'border-[var(--color-primary)] text-[var(--color-primary)]'
              : 'border-transparent text-[var(--color-ink-muted)] hover:text-[var(--color-ink)]',
          ].join(' ')}
        >
          Pendientes ({pendientes.data?.length ?? 0})
        </button>
        <button
          type="button"
          onClick={() => setTab('historial')}
          className={[
            'pb-3 text-sm font-semibold border-b-2 transition-colors',
            tab === 'historial'
              ? 'border-[var(--color-primary)] text-[var(--color-primary)]'
              : 'border-transparent text-[var(--color-ink-muted)] hover:text-[var(--color-ink)]',
          ].join(' ')}
        >
          Historial
        </button>
      </div>

      {/* Rows */}
      {isLoading && (
        <div className="text-center py-12 text-[var(--color-ink-muted)]">
          Cargando paquetes…
        </div>
      )}

      {!isLoading && (
        <div className="grid gap-2">
          {rows.map((p) => (
            <div
              key={p.id}
              className="grid grid-cols-[56px_1fr_auto_auto_auto] items-center gap-4 rounded-md border border-[var(--color-hairline)] bg-[var(--color-paper)] p-3"
            >
              {/* Photo placeholder */}
              <div className="flex h-12 w-12 items-center justify-center rounded bg-[var(--color-ink-muted,#d1cfc9)] text-xl">
                📦
              </div>

              {/* Metadata */}
              <div className="min-w-0">
                <p className="truncate font-semibold text-sm">
                  Depto {p.unidadNumero} · {p.destinatarioNombre}
                </p>
                <p className="truncate text-xs text-[var(--color-ink-muted)]">
                  {p.descripcion ?? '—'}
                </p>
              </div>

              {/* Estado badge */}
              <EstadoPaqueteBadge estado={p.estado} />

              {/* Marcar retirado */}
              {p.estado === 'recibido' && (
                <button
                  type="button"
                  onClick={() => setDialog({ type: 'retirar', paquete: p })}
                  className="rounded-md border border-[var(--color-primary)] px-3 py-1 text-xs font-semibold text-[var(--color-primary)] hover:bg-[color-mix(in_oklch,var(--color-primary)_8%,transparent)] transition-colors"
                >
                  Marcar retirado
                </button>
              )}

              {/* Devolver */}
              {p.estado === 'recibido' && (
                <button
                  type="button"
                  onClick={() => setDialog({ type: 'devolver', paquete: p })}
                  className="rounded-md border border-[var(--color-hairline)] px-3 py-1 text-xs font-semibold text-[var(--color-ink-muted)] hover:border-[var(--color-hairline-strong)] hover:text-[var(--color-ink)] transition-colors"
                >
                  Devolver
                </button>
              )}

              {/* Anular (admin/comite only, always available for recibido) */}
              {p.estado === 'recibido' && (
                <button
                  type="button"
                  onClick={() => setDialog({ type: 'anular', paquete: p })}
                  className="rounded-md border border-[var(--color-hairline)] px-3 py-1 text-xs font-semibold text-[var(--color-danger)] hover:border-[var(--color-danger)] transition-colors"
                >
                  Anular
                </button>
              )}
            </div>
          ))}

          {rows.length === 0 && (
            <div className="py-12 text-center text-[var(--color-ink-muted)]">
              {tab === 'pendientes'
                ? 'No hay paquetes pendientes de retiro.'
                : 'No hay paquetes en el historial.'}
            </div>
          )}
        </div>
      )}

      {/* Dialogs */}
      {dialog?.type === 'retirar' && (
        <RetirarDialog
          paqueteId={dialog.paquete.id}
          destinatarioNombre={dialog.paquete.destinatarioNombre}
          open
          onOpenChange={(open) => { if (!open) setDialog(null); }}
        />
      )}

      {dialog?.type === 'devolver' && (
        <DevolverDialog
          paqueteId={dialog.paquete.id}
          destinatarioNombre={dialog.paquete.destinatarioNombre}
          open
          onOpenChange={(open) => { if (!open) setDialog(null); }}
        />
      )}

      {dialog?.type === 'anular' && (
        <AnularDialog
          paqueteId={dialog.paquete.id}
          destinatarioNombre={dialog.paquete.destinatarioNombre}
          open
          onOpenChange={(open) => { if (!open) setDialog(null); }}
        />
      )}
    </PageContainer>
  );
}
